import type { FC } from 'react';
import { useEffect } from 'react';
import DaApp from '@wix/da-shared-react/pkg/DaApp';
import { RuntimeEnvironment } from '@wix/da-shared-react/pkg/types';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { isPublished } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import SharedDeviationPage from '@wix/da-shared-react/pkg/DeviationPage';
import SharedStashPage from '@wix/da-shared-react/pkg/DeviationPage/StashPage';
import { useScrollRestore } from '@wix/da-shared-react/pkg/Duperbrowse/hooks/useScrollRestore';
import DrawerLoadable from '@wix/da-feedback-shared/pkg/Drawer/DrawerLoadable';
import FeedbackPageLoadable from '@wix/da-feedback-shared/pkg/FeedbackPage/FeedbackPageLoadable';
import { DuperbrowseViewContext } from '@wix/da-shared-react/pkg/Duperbrowse/DuperbrowseViewContext';
import { PapiDeviation } from '@wix/da-papi-types';
import { composeModalRenderers } from '@wix/da-shared-react/pkg/Modals/helpers';
import { feedbackModalRenderer } from '@wix/da-feedback-shared/pkg/project-tools';
import { adoptsModalRenderer } from '@wix/da-adopts-shared/pkg/project-tools';
import { submitModalRenderer } from '@wix/da-submit-shared/pkg/project-tools';
import { muroModalRenderer } from '@wix/da-muro-ui/pkg/project-tools';
import PWAInstallBanner from '@wix/da-shared-react/pkg/PWAInstallBanner';
import SisuFunnelBanner from '@wix/da-shared-react/pkg/SisuFunnelBanner';
import { MuroDepContext } from '@wix/da-react-context/pkg/MuroDepContext';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import MicroMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Micro';
import MiniMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Mini';
import MobileMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Mobile';
import DamzDrawerLoadable from '@wix/da-damz-shared/pkg/components/Drawer/DrawerLoadable';
import DamzPageLoadable from '@wix/da-damz-shared/pkg/components/DamzPage/DamzPageLoadable';
import DamzGlobal from '@wix/da-damz-shared/pkg/redux/ModuleLoader';
import { damzModalRenderer } from '@wix/da-damz-shared/pkg/project-tools';

const MuroDepValue = {
  MicroComponent: MicroMuro,
  MiniComponent: MiniMuro,
  MobileComponent: MobileMuro,
};

export interface Props {
  language: string;
  deviation?: PapiDeviation;
  isFeedbackPageOpen?: boolean;
  isDamzPageOpen?: boolean;
  environment?: RuntimeEnvironment;
  PWAInstallBannerVisible?: boolean;
  isStash?: boolean;
  isLoggedIn?: boolean;
  isMobile?: boolean;
  appLoaded: () => void;
}

const App: FC<Props> = ({
  language,
  deviation,
  isFeedbackPageOpen,
  isDamzPageOpen,
  environment,
  appLoaded,
  isStash,
  PWAInstallBannerVisible,
  isLoggedIn,
  isMobile,
}) => {
  const mobileJoinBannerEnabled = useFlag('mobile_join_banner');
  const showJoinBanner =
    !isLoggedIn && !!isMobile && mobileJoinBannerEnabled && !isFeedbackPageOpen && !isDamzPageOpen;

  useEffect(() => {
    appLoaded();
  }, [appLoaded]);

  useScrollRestore(!!isFeedbackPageOpen || !!isDamzPageOpen);

  // isStash is based on the original route in (server/routes/stash.tsx), but
  // we need it to be false if we're rendering published deviations. It can
  // incorrectly remain true if we loaded the first page with a stash route
  // but then loaded published deviations in duperbrowse (e.g. from the sidebar)
  // so, we check both isStash -and- whether it's unpublished
  const deviationIsUnpublished = deviation && !isPublished(deviation);

  const renderMainContent = () => {
    if (isFeedbackPageOpen) {
      return <FeedbackPageLoadable />;
    }

    if (isDamzPageOpen) {
      return <DamzPageLoadable />;
    }

    return (
      <DuperbrowseViewContext.Provider value="fullscreen">
        {isStash && deviationIsUnpublished ? (
          <SharedStashPage deviation={deviation} />
        ) : (
          <SharedDeviationPage deviation={deviation} />
        )}
      </DuperbrowseViewContext.Provider>
    );
  };

  return (
    <MuroDepContext.Provider value={MuroDepValue}>
      <DaApp
        language={language}
        environment={environment}
        isDuperbrowseEnabled={false}
        disablePageWideNotifications
        ncContextProps={{
          PageComponent: FeedbackPageLoadable,
          DrawerComponent: DrawerLoadable,
        }}
        damzContextProps={{
          GlobalComponent: DamzGlobal,
          DrawerComponent: DamzDrawerLoadable,
          PageComponent: DamzPageLoadable,
        }}
        modalsRenderFunc={composeModalRenderers(
          feedbackModalRenderer,
          adoptsModalRenderer,
          muroModalRenderer,
          submitModalRenderer,
          damzModalRenderer as any
        )}
      >
        <ErrorBoundary debugComponent="DeviationApp">
          {renderMainContent()}
          {PWAInstallBannerVisible && <PWAInstallBanner />}
          {showJoinBanner && <SisuFunnelBanner visible pageType="deviation" />}
        </ErrorBoundary>
      </DaApp>
    </MuroDepContext.Provider>
  );
};
App.displayName = 'App';

export default App;
